import { PlusOutlined, ReloadOutlined, SearchOutlined } from "@ant-design/icons"
import { Button, Flex, Form, Input, Table, Tag, Typography } from "antd"
import type { TableProps } from "antd"
import { useState } from "react"
import { listOrderRefunds } from "~/api/staff"
import { RefundModal } from "./RefundModal"
import {
  applyStatusColor,
  applyStatusLabel,
  refundStatusColor,
  refundStatusLabel,
} from "./presentation"
import { formatAmount, formatDateTime, optionalValue } from "~/common/utils"
import { orderTypeLabel } from "~/pages/Staff/labels"
import { usePagedQuery } from "~/pages/Staff/usePagedQuery"
import styles from "./index.css"

const { Text, Title } = Typography

type RefundFilterForm = {
  orderNo?: string
}

export const StaffOrderRefund = () => {
  const [form] = Form.useForm<RefundFilterForm>()
  const { search, refresh, tableProps } = usePagedQuery<
    API.Staff.OrderRefundListParams,
    API.Staff.OrderRefundListItem
  >({
    fetchPage: listOrderRefunds,
    initialFilters: {},
  })
  const [modalOpen, setModalOpen] = useState(false)
  const [selectedOrderNo, setSelectedOrderNo] = useState<string>()

  const applyFilters = async () => {
    const values = await form.validateFields()
    search({ order_no: optionalValue(values.orderNo) })
  }

  const resetFilters = () => {
    form.resetFields()
    search({})
  }

  const openRefundModal = (orderNo?: string) => {
    setSelectedOrderNo(orderNo)
    setModalOpen(true)
  }

  const refreshList = async () => {
    refresh()
  }

  const columns: TableProps<API.Staff.OrderRefundListItem>["columns"] = [
    {
      title: "申请时间",
      dataIndex: "created_t",
      width: 180,
      render: formatDateTime,
    },
    {
      title: "订单号 / 退款单号",
      width: 230,
      render: (_, record) => (
        <Flex
          gap={3}
          vertical
        >
          <Text copyable>{record.order_no}</Text>
          <Text
            copyable
            type="secondary"
          >
            {record.refund_no}
          </Text>
        </Flex>
      ),
    },
    {
      title: "订单类型",
      dataIndex: "order_type",
      width: 120,
      render: (value: API.Staff.OrderRefundListItem["order_type"]) =>
        orderTypeLabel[value],
    },
    {
      title: "退款金额",
      dataIndex: "refund_amount",
      width: 110,
      render: formatAmount,
    },
    {
      title: "退款积点",
      width: 120,
      render: (_, record) =>
        `${record.refund_points} / ${record.recharge_points}`,
    },
    {
      title: "微信退款状态",
      dataIndex: "status",
      width: 160,
      render: (value: API.Staff.OrderRefundStatus) => (
        <Tag color={refundStatusColor[value]}>{refundStatusLabel[value]}</Tag>
      ),
    },
    {
      title: "权益核销",
      dataIndex: "apply_status",
      width: 110,
      render: (value: API.Staff.OrderRefundApplyStatus) => (
        <Tag color={applyStatusColor[value]}>{applyStatusLabel[value]}</Tag>
      ),
    },
    {
      title: "核销操作人",
      width: 180,
      render: (_, record) =>
        record.applied_by_staff_id ? (
          <Flex
            gap={2}
            vertical
          >
            <Text>{record.applied_by_staff_mobile || "-"}</Text>
            <Text type="secondary">{record.applied_by_staff_id}</Text>
          </Flex>
        ) : (
          "-"
        ),
    },
    {
      title: "操作",
      fixed: "right",
      width: 90,
      render: (_, record) => (
        <Button
          type="link"
          onClick={() => openRefundModal(record.order_no)}
        >
          {record.apply_status === "APPLIED" ? "查看" : "处理"}
        </Button>
      ),
    },
  ]

  return (
    <Flex
      className={styles.page}
      gap={18}
      vertical
    >
      <Flex
        align="center"
        justify="space-between"
        wrap
      >
        <Flex
          gap={6}
          vertical
        >
          <Title level={2}>订单退款</Title>
          <Text type="secondary">分步处理微信退款和本地权益核销。</Text>
        </Flex>
        <Button
          icon={<PlusOutlined />}
          size="large"
          type="primary"
          onClick={() => openRefundModal()}
        >
          发起退款
        </Button>
      </Flex>

      <section className={styles.filterPanel}>
        <Form<RefundFilterForm>
          form={form}
          onFinish={() => void applyFilters()}
        >
          <Flex gap={10}>
            <Form.Item
              className={styles.orderInput}
              name="orderNo"
              rules={[{ max: 32, message: "订单号不能超过32个字符" }]}
            >
              <Input
                allowClear
                placeholder="按订单号查询"
              />
            </Form.Item>
            <Button
              htmlType="submit"
              icon={<SearchOutlined />}
              type="primary"
            >
              查询
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={resetFilters}
            >
              重置
            </Button>
          </Flex>
        </Form>
      </section>

      <section className={styles.listPanel}>
        <Table<API.Staff.OrderRefundListItem>
          columns={columns}
          {...tableProps}
          rowKey="id"
          scroll={{ x: 1310 }}
        />
      </section>

      <RefundModal
        initialOrderNo={selectedOrderNo}
        open={modalOpen}
        onChanged={refreshList}
        onClose={() => setModalOpen(false)}
      />
    </Flex>
  )
}
